import { HTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

import { BadgeProps } from '@/decodo/components/atoms/badge';
import Button from '@/decodo/components/atoms/Button';
import { NextImageProps } from '@/decodo/components/atoms/Image';
import LinkButton from '@/decodo/components/atoms/LinkButton';
import { gradientDecodoBorderStyling } from '@/styles/gradient';

import IconChevronDown from '~/icons/chevron-down.svg';

export type BaseHeaderItemProps = {
  badge?: React.ReactNode;
  showDropdown?: boolean;
  expanded?: boolean;
  onItemClick?: (
    params: { title: string } | { url: string; keepMenuOpen?: boolean }
  ) => void;
  className?: string;
  classNames?: {
    root?: string;
    inner?: string;
  };
  href?: string;
} & HTMLAttributes<undefined>;

export type HeaderItemProps = BaseHeaderItemProps &
  (
    | { icon: React.ReactNode; title?: string }
    | { icon?: React.ReactNode; title: string }
    | { icon: React.ReactNode; title: string }
  );

export function HeaderItem({
  title,
  icon: Icon,
  badge: Badge,
  expanded,
  showDropdown = true,
  onItemClick,
  className,
  classNames,
  href,
  ...rest
}: HeaderItemProps) {
  const classes = {
    ...buttonClasses,
    root: cn(buttonClasses.root, classNames?.root),
    inner: cn(buttonClasses.inner, classNames?.inner),
  };

  const handleClick = () => onItemClick?.({ title: title ?? '' });
  if (!!href) {
    return (
      <LinkButton
        href={href}
        data-state={expanded ? 'open' : 'closed'}
        aria-expanded={expanded}
        noStyles
        classNames={classes}
        leftSection={Icon}
        rightSection={!!showDropdown ? <IconChevronDown /> : null}
        // eslint-disable-next-line
        // @ts-ignore
        onClick={handleClick}
        {...rest}
      >
        {title}
        {Badge}
      </LinkButton>
    );
  }
  return (
    <Button
      data-state={expanded ? 'open' : 'closed'}
      aria-expanded={expanded}
      variant='text'
      noStyles
      classNames={classes}
      leftSection={Icon}
      rightSection={
        !!showDropdown ? (
          <IconChevronDown className='group-data-[hovered=true]/header-list-item:text-gray-500' />
        ) : null
      }
      // eslint-disable-next-line
      // @ts-ignore
      onClick={handleClick}
      {...rest}
    >
      {title}
      {Badge}
    </Button>
  );
}

const borderClasses = gradientDecodoBorderStyling({
  color: 'gradient-main',
  borderWidth: 'p-px',
  borderRadius: 'rounded-none',
});

const buttonClasses = {
  root: ` group/header-item headerLg:h-full font-serif flex items-center gap-x-1 w-full headerLg:w-auto leading-[1.75rem] py-6 headerLg:py-[22px] select-text
    justify-between relative focus-visible:outline-none
    text-base-dark-black headerLg:text-base-dark-white headerLg:bg-transparent transition-colors duration-300
    group-data-[hovered=true]/header-list-item:text-gray-dark-500
    ${borderClasses}
    before:absolute before:inset-x-0 before:bottom-0 before:top-auto before:h-px
    before:origin-bottom before:scale-x-95 before:scale-y-0 
    before:opacity-0 before:duration-300 before:headerLg:opacity-100
    hover:before:scale-100
    data-[state=open]:before:scale-100
    focus-visible:before:scale-100
    `,
  inner:
    'flex items-center gap-x-2 text-body-l grow duration-300 text-nowrap group-data-[hovered=true]/header-list-item:text-gray-dark-500 group-data-[state=open]/header-item:text-brand-dark-500 headerLg:group-data-[state=open]/header-item:text-current',
  leftSection:
    'group-data-[state=open]/header-item:text-brand-dark-500 headerLg:group-data-[state=open]/header-item:text-current',
  rightSection:
    'w-6 h-6 origin-center duration-300 group-data-[state=open]/header-item:-rotate-180',
};

export const badgeInnerProps = {
  size: 's',
  variant: 'outlined',
  classNames: {
    root: 'uppercase select-none group-data-[hovered=true]/header-list-item:text-gray-dark-500 text-base-dark-black headerLg:text-base-dark-white',
  },
} satisfies BadgeProps;

export const iconInnerProps = {
  classNames: { image: 'w-6 h-6' },
} satisfies Partial<NextImageProps>;
